import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

interface PartnerPaymentModalProps {
  open: boolean;
  clientSecret: string;
  listingId: string;
  paymentIntentId: string;
  entryFee: number;
  position: 'drive' | 'reves';
  onSuccess: (result: { matched: boolean; eventId: string }) => void;
  onCancel: () => void;
}

const PaymentForm = ({
  listingId,
  paymentIntentId,
  entryFee,
  position,
  onSuccess,
  onCancel,
}: Omit<PartnerPaymentModalProps, 'open' | 'clientSecret'>) => {
  const stripe = useStripe();
  const elements = useElements();
  const { language } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || (language === 'es' ? 'Error al procesar el pago' : 'Payment processing error'));
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'requires_capture') {
        // Authorization hold successful — confirm on backend
        const { data: confirmData, error: confirmError } = await supabase.functions.invoke('confirm-partner-payment', {
          body: { listingId, paymentIntentId },
        });

        if (confirmError) {
          console.error('[PartnerPaymentModal] confirm-partner-payment error:', confirmError);
          let errMsg = language === 'es' ? 'Error al confirmar la búsqueda' : 'Error confirming the search';
          try {
            const body = await (confirmError as any).context?.json?.();
            if (body?.error) errMsg += `: ${body.error}`;
          } catch {}
          setError(errMsg);
          setProcessing(false);
          return;
        }

        onSuccess({ matched: confirmData?.matched ?? false, eventId: confirmData?.eventId ?? '' });
      } else {
        setError(language === 'es' ? 'Estado de pago inesperado' : 'Unexpected payment status');
        setProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || (language === 'es' ? 'Error inesperado' : 'Unexpected error'));
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
        <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
        <p>
          {language === 'es'
            ? 'Solo se retendrá el monto. No se cobrará hasta que encuentres pareja.'
            : 'The amount will only be held. You won\'t be charged until a partner is found.'}
        </p>
      </div>

      <div className="text-center space-y-1">
        <p className="text-sm text-muted-foreground">
          {language === 'es' ? 'Retención por persona' : 'Hold per person'}
        </p>
        <p className="text-2xl font-bold text-foreground">${entryFee / 2} MXN</p>
        <p className="text-xs text-muted-foreground">
          {language === 'es'
            ? `Posición: ${position === 'drive' ? 'Drive' : 'Revés'}`
            : `Position: ${position === 'drive' ? 'Drive (Forehand)' : 'Backhand'}`}
        </p>
      </div>

      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />

      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={processing}
        >
          {language === 'es' ? 'Cancelar' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={!stripe || !elements || processing}
        >
          {processing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <CreditCard className="h-4 w-4 mr-2" />
          )}
          {language === 'es' ? 'Autorizar' : 'Authorize'}
        </Button>
      </div>
    </form>
  );
};

const PartnerPaymentModal = ({
  open,
  clientSecret,
  listingId,
  paymentIntentId,
  entryFee,
  position,
  onSuccess,
  onCancel,
}: PartnerPaymentModalProps) => {
  const { language } = useLanguage();

  if (!stripePromise) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'es' ? 'Error de configuración' : 'Configuration error'}</DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'La clave de Stripe no está configurada. Contacta al administrador.'
                : 'Stripe key is not configured. Contact the administrator.'}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {language === 'es' ? 'Retención de pago' : 'Payment hold'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es'
              ? 'Ingresa los datos de tu tarjeta para reservar tu lugar en la lista de espera.'
              : 'Enter your card details to reserve your spot on the waitlist.'}
          </DialogDescription>
        </DialogHeader>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#3D6B4F',
              },
            },
          }}
        >
          <PaymentForm
            listingId={listingId}
            paymentIntentId={paymentIntentId}
            entryFee={entryFee}
            position={position}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerPaymentModal;
